import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Sidebar from './components/layout/Sidebar';
import ScreenMirrorPanel from './components/panels/ScreenMirrorPanel';
import GeminiChatPanel from './components/panels/GeminiChatPanel';
import ControlCenter from './pages/ControlCenter';
import Devices from './pages/Devices';
import DeviceInfo from './pages/DeviceInfo';
import StatisticsNew from './pages/StatisticsNew';
import Database from './pages/Database';
import Workflows from './pages/Workflows';
import WorkflowBuilder from './pages/WorkflowBuilder';
import Sessions from './pages/Sessions';
import Accounts from './pages/Accounts';
import AccountProfile from './pages/AccountProfile';
import Search from './pages/Search';
import AutomationDashboard from './pages/AutomationDashboard';
import Settings from './pages/Settings';
import Gmail from './pages/Gmail';
import GmailAutomation from './pages/GmailAutomation';
import GoogleMapsAutomation from './pages/GoogleMapsAutomation';
import GoogleAutomation from './pages/GoogleAutomation';
import GoogleAnalytics from './pages/GoogleAnalytics';
import TikTokAutomation from './pages/TikTokAutomation';
import TikTokAccount from './pages/TikTokAccount';
import TikTokAnalytics from './pages/TikTokAnalytics';
import TwitterAccount from './pages/TwitterAccount';
import TwitterAutomation from './pages/TwitterAutomation';
import TwitterAnalytics from './pages/TwitterAnalytics';
import TinderAccount from './pages/TinderAccount';
import TinderAutomation from './pages/TinderAutomation';
import FleetLeads from './pages/FleetLeads';
import ActionsLog from './pages/ActionsLog';
import ErrorLogs from './components/ErrorLogs';
import PlaceholderPage from './pages/PlaceholderPage';
import CrmProspects from './pages/CrmProspects';
import ScriptFlowsPage from './pages/ScriptFlowsPage';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/auth/LoginPage';
import ActivationPage from './components/auth/ActivationPage';
import DebugConsole from './components/panels/DebugConsole';

const GlobalStyle = createGlobalStyle`
  :root {
    --bg: ${props => props.$theme?.background || '#ffffff'};
    --surface: ${props => props.$theme?.surface || '#f8f7ff'};
    --surface-hover: ${props => props.$theme?.surfaceHover || '#f0eeff'};
    --border: ${props => props.$theme?.border || '#e8e5f5'};
    --primary: ${props => props.$theme?.primary || '#a591ef'};
    --secondary: ${props => props.$theme?.secondary || '#8b74e0'};
    --accent: ${props => props.$theme?.accent || '#c4b5fd'};
    --text: ${props => props.$theme?.text || '#a591ef'};
    --text-primary: ${props => props.$theme?.textPrimary || '#6b5bb5'};
    --text-secondary: ${props => props.$theme?.textSecondary || '#8b7fc7'};
    --text-muted: ${props => props.$theme?.textMuted || '#b8aee0'};
    --text-dark: ${props => props.$theme?.textDark || '#4a3d8f'};
    --card-bg: ${props => props.$theme?.cardBg || '#ffffff'};
    --card-border: ${props => props.$theme?.cardBorder || '#e8e5f5'};
    --input-bg: ${props => props.$theme?.inputBg || '#ffffff'};
    --input-border: ${props => props.$theme?.inputBorder || '#e0ddf0'};
    --input-focus: ${props => props.$theme?.inputFocus || '#a591ef'};
    --btn-primary: ${props => props.$theme?.buttonPrimary || '#a591ef'};
    --btn-primary-hover: ${props => props.$theme?.buttonPrimaryHover || '#8b74e0'};
    --btn-secondary: ${props => props.$theme?.buttonSecondary || '#f0eeff'};
    --btn-text: ${props => props.$theme?.buttonText || '#ffffff'};
    --success: ${props => props.$theme?.success || '#7cc88e'};
    --warning: ${props => props.$theme?.warning || '#e8b84a'};
    --error: ${props => props.$theme?.error || '#e87272'};
    --info: ${props => props.$theme?.info || '#72b8e8'};
    --gradient: ${props => props.$theme?.gradient || 'linear-gradient(135deg, #a591ef, #c4b5fd)'};
    --shadow: ${props => props.$theme?.shadow || '0 2px 8px rgba(165, 145, 239, 0.12)'};
    --shadow-hover: ${props => props.$theme?.shadowHover || '0 4px 16px rgba(165, 145, 239, 0.2)'};
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background: var(--bg);
    color: var(--text-primary);
    overflow-x: hidden;
    transition: background 0.3s ease, color 0.3s ease;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--surface);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--secondary);
  }
`;

const AppContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--bg);
  transition: background 0.3s ease;
`;

const MainContent = styled.main`
  padding: 20px;
  margin-left: ${p => p.$sidebarW || 410}px;
  margin-right: ${p => {
    let r = 0;
    if (p.$mirrorOpen) r += (p.$mirrorWide ? 520 : 320);
    if (p.$debugOpen) r += 420;
    if (p.$geminiOpen) r += 380;
    return r + 'px';
  }};
  width: calc(100vw - ${p => p.$sidebarW || 410}px - ${p => {
    let r = 0;
    if (p.$mirrorOpen) r += (p.$mirrorWide ? 520 : 320);
    if (p.$debugOpen) r += 420;
    if (p.$geminiOpen) r += 380;
    return r + 'px';
  }});
  background: var(--surface);
  border-radius: 20px 0 0 20px;
  border-left: 1px solid var(--border);
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  transition: background 0.3s ease, border-color 0.3s ease, margin-right 0.25s ease, width 0.25s ease, margin-left 0.25s ease;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-right: 0;
    width: 100vw;
    border-radius: 0;
    border-left: none;
    padding: 16px 12px 80px 12px;
  }
`;

const pages = {
  'impulse-bot': ControlCenter,
  'devices': Devices,
  'device-info': DeviceInfo,
  'statistics': StatisticsNew,
  'cibles': StatisticsNew,
  'database': Database,
  'workflows': Workflows,
  'workflow-builder': WorkflowBuilder,
  'sessions': Sessions,
  'accounts': AccountProfile,
  'accounts-list': Accounts,
  'search': Search,
  'targets': AutomationDashboard,
  'settings': Settings,
  'gmail': Gmail,
  'gmail-auto': GmailAutomation,
  'tiktok-auto': TikTokAutomation,
  'tiktok-account': TikTokAccount,
  'tiktok-analytics': TikTokAnalytics,
  'google-auto': GoogleAutomation,
  'google-maps': GoogleMapsAutomation,
  'google-analytics': GoogleAnalytics,
  'twitter-account': TwitterAccount,
  'twitter-auto': TwitterAutomation,
  'twitter-analytics': TwitterAnalytics,
  'tinder-account': TinderAccount,
  'tinder-auto': TinderAutomation,
  'fleet-leads': FleetLeads,
  'actions-log': ActionsLog,
  'error-logs': ErrorLogs,
  'crm': CrmProspects,
  'script-flows': ScriptFlowsPage
};

function AuthenticatedApp({ modules = [] }) {
  const [activeTab, setActiveTab] = useState(() => {
    // Toujours restaurer le dernier onglet visité
    const lastTab = localStorage.getItem('activeTab');
    const lockedTab = localStorage.getItem('lockedTab');
    return lockedTab || lastTab || 'statistics';
  });
  const [isTabLocked] = useState(() => {
    return localStorage.getItem('lockedTab') !== null;
  });
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [mirrorOpen, setMirrorOpen] = useState(false);
  const [mirrorWide, setMirrorWide] = useState(false);
  const [geminiOpen, setGeminiOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try { return localStorage.getItem('qlickr_narrow_collapsed') === 'true' ? (48 + 250) : (160 + 250); } catch (_) { return 410; }
  });
  const [activeWorkstation, setActiveWorkstation] = useState('local'); // 'all' | 'local' | ws_id string

  // Sync selectedAccountId when device changes (so cibles/statistics/accounts pages update)
  useEffect(() => {
    if (selectedDevice?.account_id) {
      setSelectedAccountId(selectedDevice.account_id);
    }
  }, [selectedDevice?.device_id, selectedDevice?.account_id]);

  // Sauvegarder l'onglet actif dans localStorage (persistance au reload)
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
    if (isTabLocked) {
      localStorage.setItem('lockedTab', activeTab);
    }
  }, [activeTab, isTabLocked]);

  const handleTabChange = (tabId) => {
    // 'phone-cast' toggles the mirror panel instead of navigating
    if (tabId === 'phone-cast') {
      setMirrorOpen(prev => !prev);
      return;
    }
    setActiveTab(tabId);
  };



  const handleNavigateToStats = (accountId) => {
    setSelectedAccountId(accountId);
    setActiveTab('statistics');
  };

  const handleNavigateToAutomation = (device) => {
    setSelectedDevice(device);
    setActiveTab('targets');
  };

  const ActiveComponent = pages[activeTab] || PlaceholderPage;

  const renderPage = () => {
    if (activeTab === 'accounts') {
      return <ActiveComponent selectedDevice={selectedDevice} />;
    }
    if (activeTab === 'accounts-list') {
      return <ActiveComponent onNavigateToStats={handleNavigateToStats} onNavigateToAutomation={handleNavigateToAutomation} onSelectDevice={setSelectedDevice} setMirrorOpen={setMirrorOpen} activeWorkstation={activeWorkstation} />;
    }
    if (activeTab === 'statistics') {
      const accountId = selectedAccountId || selectedDevice?.account_id || null;
      return <ActiveComponent selectedAccountId={accountId} defaultTab="overview" />;
    }
    if (activeTab === 'cibles') {
      const accountId = selectedAccountId || selectedDevice?.account_id || null;
      return <ActiveComponent selectedAccountId={accountId} defaultTab="targets" onSwitchAccount={(newAccountId) => {
        setSelectedAccountId(newAccountId);
      }} />;
    }
    if (activeTab === 'device-info' || activeTab === 'targets') {
      return <ActiveComponent selectedDevice={selectedDevice} onNavigate={handleTabChange} activeTab={activeTab} />;
    }
    if (activeTab === 'gmail-auto' || activeTab === 'tiktok-auto' || activeTab === 'google-auto' || activeTab === 'twitter-auto' || activeTab === 'tinder-auto') {
      return <ActiveComponent selectedDevice={selectedDevice} onNavigate={handleTabChange} activeTab={activeTab} />;
    }
    if (activeTab === 'gmail' || activeTab === 'tiktok-account' || activeTab === 'tiktok-analytics' || activeTab === 'google-analytics' || activeTab === 'twitter-account' || activeTab === 'twitter-analytics' || activeTab === 'tinder-account') {
      return <ActiveComponent selectedDevice={selectedDevice} onNavigate={handleTabChange} activeTab={activeTab} tabId={activeTab} />;
    }
    // Pass onSelectDevice and setMirrorOpen to Devices page
    if (activeTab === 'devices') {
      return <ActiveComponent selectedDevice={selectedDevice} onSelectDevice={setSelectedDevice} setMirrorOpen={setMirrorOpen} activeWorkstation={activeWorkstation} onWorkstationChange={setActiveWorkstation} />;
    }
    // ControlCenter (Centre de Contrôle) needs onSelectDevice for clickable device badges
    if (activeTab === 'impulse-bot') {
      return <ActiveComponent onSelectDevice={setSelectedDevice} setMirrorOpen={setMirrorOpen} />;
    }
    // Workflow Builder needs onSelectDevice + setMirrorOpen for Cast buttons on restarters
    if (activeTab === 'workflow-builder') {
      return <ActiveComponent selectedDevice={selectedDevice} onSelectDevice={setSelectedDevice} setMirrorOpen={setMirrorOpen} />;
    }
    // Script Flows standalone page
    if (activeTab === 'script-flows') {
      return <ActiveComponent selectedDevice={selectedDevice} />;
    }
    // Placeholder pages get the tab name for display
    if (ActiveComponent === PlaceholderPage) {
      return <ActiveComponent tabId={activeTab} selectedDevice={selectedDevice} />;
    }
    return <ActiveComponent selectedDevice={selectedDevice} />;
  };

  return (
    <AppContainer>
      <Sidebar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        onDebugToggle={() => setDebugOpen(prev => !prev)}
        onGeminiToggle={() => setGeminiOpen(prev => !prev)}
        selectedDevice={selectedDevice}
        onSelectDevice={setSelectedDevice}
        mirrorOpen={mirrorOpen}
        activeWorkstation={activeWorkstation}
        onWorkstationChange={setActiveWorkstation}
        modules={modules}
        onSidebarWidthChange={setSidebarWidth}
      />
      <MainContent $mirrorOpen={mirrorOpen} $mirrorWide={mirrorWide} $debugOpen={debugOpen} $geminiOpen={geminiOpen} $sidebarW={sidebarWidth}>
        {renderPage()}
      </MainContent>
      <DebugConsole 
        isOpen={debugOpen} 
        onClose={() => setDebugOpen(false)}
        rightOffset={'0'}
      />
      <ScreenMirrorPanel
        isOpen={mirrorOpen}
        onClose={() => setMirrorOpen(false)}
        selectedDevice={selectedDevice}
        rightOffset={(debugOpen ? 420 : 0) + 'px'}
        onWideModeChange={setMirrorWide}
      />
      <GeminiChatPanel
        isOpen={geminiOpen}
        onClose={() => setGeminiOpen(false)}
        rightOffset={(debugOpen ? 420 : 0) + (mirrorOpen ? (mirrorWide ? 520 : 320) : 0) + 'px'}
      />
    </AppContainer>
  );
}

function AppContent() {
  const { theme } = useTheme();
  const { isAuthenticated, loading } = useAuth();
  const [licenseState, setLicenseState] = useState(null); // null=loading, false=no license, string[]=modules

  useEffect(() => {
    if (!isAuthenticated) { setLicenseState(null); return; }
    fetch('/api/licensing/status')
      .then(r => r.json())
      .then(d => setLicenseState(d.activated ? (d.modules || []) : false))
      .catch(() => setLicenseState(false));
  }, [isAuthenticated]);

  if (loading || (isAuthenticated && licenseState === null)) return null;

  let content;
  if (!isAuthenticated) {
    content = <LoginPage />;
  } else if (licenseState === false) {
    content = <ActivationPage onActivated={modules => setLicenseState(modules)} />;
  } else {
    content = <AuthenticatedApp modules={licenseState} />;
  }

  return (
    <>
      <GlobalStyle $theme={theme} />
      {content}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;



